import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import { FormLayout } from "@/components/Forms";
import useOnCloseWithConfirmation from "@/hooks/useOnCloseWithConfirmation";
import FormField from "@/components/Forms/FormField";
import { ReportsGroupResource } from "@/types/resources";
import RestrictionsField from "./RestrictionsField";

interface FormDataT {
  name: string;
  note: string;
  is_restricted: boolean;
}

interface Props {
  onClose: () => void;
  editableGroup?: ReportsGroupResource;
}

const ReportsGroupForm: FC<Props> = ({ onClose, editableGroup }) => {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableGroup?.name ?? "",
      note: editableGroup?.note ?? "",
      is_restricted: !!editableGroup?.isRestricted,
    },
  });

  const alerts = useAlerts();

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (editableGroup) {
          await api.put<FormDataT, { id: number }>(
            `/v1/reports-groups/${editableGroup.id}`,
            data
          );
        } else {
          await api.post<FormDataT, { id: number }>("/v1/reports-groups", data);
        }

        queryClient.invalidateQueries(["reports_groups"]);

        alerts.success(
          `Group was successfully ${!!editableGroup ? "updated" : "created"}`
        );

        onClose();
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${editableGroup ? "Edit" : "New"} group`}
        onClose={closeDialog}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="name"
                type="text"
                label="Group name"
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                type="textarea"
                label="Note"
              />
              <RestrictionsField<FormDataT>
                name="is_restricted"
                control={control}
                description="This group of reports will be accessible for limited Backbone roles
                only."
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {editableGroup ? "Update" : "Create"}
            </Button>
            <Button color="secondary" onClick={closeDialog} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ReportsGroupForm;
